import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CheckCircle, AlertCircle, Upload, User, Building, CreditCard,
  FileText, Shield, XCircle, Loader, ArrowRight, ArrowLeft, Info
} from 'lucide-react';
import './KYCWizard.css';

const KYCWizard = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [kycData, setKycData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form data for each level
  const [basicInfo, setBasicInfo] = useState({
    businessName: '',
    businessType: 'INDIVIDUAL',
    isRegisteredBusiness: false,
    tradingAs: '',
    phoneNumber: '',
    bankName: '',
    bankCode: '',
    accountNumber: ''
  });

  const [identityInfo, setIdentityInfo] = useState({
    idType: 'NATIONAL_ID',
    idNumber: '',
    businessAddress: '',
    city: '',
    state: ''
  });

  const [businessInfo, setBusinessInfo] = useState({
    bvn: '',
    registrationNumber: '',
    taxIdentificationNumber: ''
  });

  const [documents, setDocuments] = useState({
    idDocument: null,
    idDocumentBack: null,
    passportPhoto: null,
    cacDocument: null,
    utilityBill: null
  });

  // Nigerian banks
  const banks = [
    { name: 'Access Bank', code: '044' },
    { name: 'Citibank', code: '023' },
    { name: 'Ecobank', code: '050' },
    { name: 'Fidelity Bank', code: '070' },
    { name: 'First Bank of Nigeria', code: '011' },
    { name: 'First City Monument Bank (FCMB)', code: '214' },
    { name: 'Globus Bank', code: '00103' },
    { name: 'Guaranty Trust Bank (GTBank)', code: '058' },
    { name: 'Heritage Bank', code: '030' },
    { name: 'Keystone Bank', code: '082' },
    { name: 'Kuda Bank', code: '50211' },
    { name: 'Opay', code: '999992' },
    { name: 'Palmpay', code: '999991' },
    { name: 'Polaris Bank', code: '076' },
    { name: 'Providus Bank', code: '101' },
    { name: 'Stanbic IBTC Bank', code: '221' },
    { name: 'Standard Chartered Bank', code: '068' },
    { name: 'Sterling Bank', code: '232' },
    { name: 'Union Bank', code: '032' },
    { name: 'United Bank for Africa (UBA)', code: '033' },
    { name: 'Unity Bank', code: '215' },
    { name: 'Wema Bank', code: '035' },
    { name: 'Zenith Bank', code: '057' }
  ];

  useEffect(() => {
    fetchKYCStatus();
  }, []);

  const fetchKYCStatus = async () => {
    try {
      const response = await axios.get('/api/kyc/status');
      if (response.data.success) {
        setKycData(response.data.kyc);
        const kyc = response.data.kyc;

        // Pre-fill address fields from existing record so merchants who supplied
        // these during onboarding (Phase O1b) don't re-enter them at Level 2.
        if (kyc.businessAddress || kyc.city || kyc.state) {
          setIdentityInfo(prev => ({
            ...prev,
            businessAddress: prev.businessAddress || kyc.businessAddress || '',
            city: prev.city || kyc.city || '',
            state: prev.state || kyc.state || ''
          }));
        }

        // Determine which step to show based on KYC level and status
        if (kyc.level === 'LEVEL_0' || (kyc.level === 'LEVEL_1' && kyc.status !== 'APPROVED')) {
          // Level 0 or Level 1 not yet approved -> show step 1
          setCurrentStep(1);
        } else if (kyc.level === 'LEVEL_1' && kyc.status === 'APPROVED') {
          // Level 1 approved -> show step 2 to upgrade to Level 2
          setCurrentStep(2);
        } else if (kyc.level === 'LEVEL_2' && kyc.status === 'APPROVED') {
          // Level 2 approved -> show step 3 to upgrade to Level 3
          setCurrentStep(3);
        } else if (kyc.level === 'LEVEL_3' && kyc.status === 'APPROVED') {
          // Level 3 approved -> show completion
          setCurrentStep(4);
        } else if (kyc.status === 'PENDING_REVIEW' || kyc.status === 'UNDER_REVIEW') {
          // Pending review -> show completion with pending message
          setCurrentStep(4);
        } else if (kyc.status === 'RESUBMISSION_REQUIRED') {
          // Rejected -> show the step they need to resubmit based on level
          if (kyc.level === 'LEVEL_0') setCurrentStep(1);
          else if (kyc.level === 'LEVEL_1') setCurrentStep(2);
          else if (kyc.level === 'LEVEL_2') setCurrentStep(3);
        } else {
          // Default fallback
          setCurrentStep(1);
        }
      }
    } catch (err) {
      console.error('Failed to fetch KYC status:', err);
    }
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      setDocuments({ ...documents, [field]: file });
    }
  };

  const submitBasicInfo = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post('/api/kyc/submit/basic', basicInfo);
      if (response.data.success) {
        setSuccess('Basic information submitted successfully! You can now receive payments up to ₦100,000 daily.');
        await fetchKYCStatus();
        setTimeout(() => {
          setCurrentStep(2);
          setSuccess('');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit basic information');
    } finally {
      setLoading(false);
    }
  };

  const submitIdentityInfo = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('idType', identityInfo.idType);
      formData.append('idNumber', identityInfo.idNumber);
      // For National ID, the idNumber IS the NIN, so copy it to nin field
      formData.append('nin', identityInfo.idType === 'NATIONAL_ID' ? identityInfo.idNumber : '');
      formData.append('businessAddress', identityInfo.businessAddress);
      formData.append('city', identityInfo.city);
      formData.append('state', identityInfo.state);

      if (documents.idDocument) formData.append('idDocument', documents.idDocument);
      if (documents.idDocumentBack) formData.append('idDocumentBack', documents.idDocumentBack);
      if (documents.passportPhoto) formData.append('passportPhoto', documents.passportPhoto);

      const response = await axios.post('/api/kyc/submit/identity', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setSuccess('Identity verification submitted! Your KYC is now pending admin review.');
        await fetchKYCStatus();
        setTimeout(() => {
          setCurrentStep(3);
          setSuccess('');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit identity verification');
    } finally {
      setLoading(false);
    }
  };

  const submitBusinessInfo = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('bvn', businessInfo.bvn);
      if (basicInfo.isRegisteredBusiness) {
        formData.append('registrationNumber', businessInfo.registrationNumber);
      }
      if (businessInfo.taxIdentificationNumber) {
        formData.append('taxIdentificationNumber', businessInfo.taxIdentificationNumber);
      }

      if (documents.cacDocument) formData.append('cacDocument', documents.cacDocument);
      if (documents.utilityBill) formData.append('utilityBill', documents.utilityBill);

      const response = await axios.post('/api/kyc/submit/business', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setSuccess('Full KYC submitted successfully! Pending final admin approval for unlimited transactions.');
        await fetchKYCStatus();
        setTimeout(() => {
          setCurrentStep(4);
          setSuccess('');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit business verification');
    } finally {
      setLoading(false);
    }
  };

  const verifyBankAccount = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Use the new verify-bank endpoint that accepts account details
      const response = await axios.post('/api/kyc/verify-bank', {
        accountNumber: basicInfo.accountNumber,
        bankCode: basicInfo.bankCode,
        bankName: basicInfo.bankName
      });

      if (response.data.success) {
        setSuccess(`Bank account verified! Account Name: ${response.data.data.accountName}`);
        // Update the basic info with the verified account name
        setBasicInfo(prev => ({
          ...prev,
          accountName: response.data.data.accountName
        }));
        await fetchKYCStatus();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Bank verification failed. Please check your details.');
    } finally {
      setLoading(false);
    }
  };

  const getStepStatus = (step) => {
    if (!kycData) return 'pending';
    const levelNum = parseInt(kycData.level.replace('LEVEL_', ''));
    if (levelNum >= step) return 'completed';
    if (currentStep === step) return 'active';
    return 'pending';
  };

  const renderStepIndicator = () => {
    const steps = [
      { number: 1, title: 'Basic Info', icon: User },
      { number: 2, title: 'Identity', icon: FileText },
      { number: 3, title: 'Business', icon: Building },
      { number: 4, title: 'Complete', icon: CheckCircle }
    ];

    return (
      <div className="kyc-steps">
        {steps.map((step, index) => {
          const status = getStepStatus(step.number);
          const Icon = step.icon;
          return (
            <div key={step.number} className={`kyc-step ${status}`}>
              <div className="kyc-step-icon">
                <Icon size={24} />
              </div>
              <div className="kyc-step-info">
                <span className="kyc-step-number">Step {step.number}</span>
                <span className="kyc-step-title">{step.title}</span>
              </div>
              {index < steps.length - 1 && <div className="kyc-step-connector" />}
            </div>
          );
        })}
      </div>
    );
  };

  const renderStep1 = () => (
    <form onSubmit={submitBasicInfo} className="kyc-form">
      <div className="kyc-form-header">
        <h2>Level 1: Basic Information</h2>
        <p>Start receiving payments up to ₦100,000 daily</p>
      </div>

      <div className="kyc-form-group">
        <label>Business Name *</label>
        <input
          type="text"
          value={basicInfo.businessName}
          onChange={(e) => setBasicInfo({ ...basicInfo, businessName: e.target.value })}
          required
          placeholder="Enter your business name"
        />
      </div>

      <div className="kyc-form-group">
        <label>Business Type *</label>
        <select
          value={basicInfo.businessType}
          onChange={(e) => setBasicInfo({ ...basicInfo, businessType: e.target.value })}
          required
        >
          <option value="INDIVIDUAL">Individual/Sole Proprietor</option>
          <option value="SOLE_PROPRIETORSHIP">Sole Proprietorship</option>
          <option value="LIMITED_LIABILITY_COMPANY">Limited Liability Company</option>
          <option value="PARTNERSHIP">Partnership</option>
          <option value="OTHER">Other</option>
        </select>
      </div>

      <div className="kyc-form-group">
        <label>
          <input
            type="checkbox"
            checked={basicInfo.isRegisteredBusiness}
            onChange={(e) => setBasicInfo({ ...basicInfo, isRegisteredBusiness: e.target.checked })}
          />
          I have a registered business with CAC
        </label>
        <small className="kyc-hint">
          <Info size={14} /> Check this if you have CAC registration. If you're just testing your business idea, leave this unchecked.
        </small>
      </div>

      {!basicInfo.isRegisteredBusiness && (
        <div className="kyc-form-group">
          <label>Trading As</label>
          <input
            type="text"
            value={basicInfo.tradingAs}
            onChange={(e) => setBasicInfo({ ...basicInfo, tradingAs: e.target.value })}
            placeholder="What name are you trading under?"
          />
        </div>
      )}

      <div className="kyc-form-group">
        <label>Phone Number *</label>
        <input
          type="tel"
          value={basicInfo.phoneNumber}
          onChange={(e) => setBasicInfo({ ...basicInfo, phoneNumber: e.target.value })}
          required
          placeholder="+234"
        />
      </div>

      <div className="kyc-divider">Bank Account Details</div>

      <div className="kyc-form-group">
        <label>Bank Name *</label>
        <select
          value={basicInfo.bankCode}
          onChange={(e) => {
            const selectedBank = banks.find(b => b.code === e.target.value);
            setBasicInfo({
              ...basicInfo,
              bankCode: e.target.value,
              bankName: selectedBank?.name || ''
            });
          }}
          required
        >
          <option value="">Select your bank</option>
          {banks.map(bank => (
            <option key={bank.code} value={bank.code}>{bank.name}</option>
          ))}
        </select>
      </div>

      <div className="kyc-form-group">
        <label>Account Number *</label>
        <input
          type="text"
          value={basicInfo.accountNumber}
          onChange={(e) => setBasicInfo({ ...basicInfo, accountNumber: e.target.value })}
          required
          maxLength="10"
          placeholder="10-digit account number"
        />
        {basicInfo.accountNumber.length === 10 && basicInfo.bankCode && (
          <button
            type="button"
            onClick={verifyBankAccount}
            className="kyc-verify-btn"
            disabled={loading}
          >
            {loading ? <Loader className="spin" size={16} /> : <Shield size={16} />}
            Verify Account
          </button>
        )}
      </div>

      {error && (
        <div className="kyc-alert error">
          <XCircle size={16} />
          {error}
        </div>
      )}

      {success && (
        <div className="kyc-alert success">
          <CheckCircle size={16} />
          {success}
        </div>
      )}

      <div className="kyc-form-actions">
        <button type="submit" className="kyc-btn primary" disabled={loading}>
          {loading ? <Loader className="spin" size={20} /> : 'Continue'}
          {!loading && <ArrowRight size={20} />}
        </button>
      </div>
    </form>
  );

  const renderStep2 = () => (
    <form onSubmit={submitIdentityInfo} className="kyc-form">
      <div className="kyc-form-header">
        <h2>Level 2: Identity Verification</h2>
        <p>Increase your daily limit to ₦500,000</p>
      </div>

      <div className="kyc-form-group">
        <label>ID Type *</label>
        <select
          value={identityInfo.idType}
          onChange={(e) => setIdentityInfo({ ...identityInfo, idType: e.target.value })}
          required
        >
          <option value="NATIONAL_ID">National ID (NIN)</option>
          <option value="DRIVERS_LICENSE">Driver's License</option>
          <option value="INTERNATIONAL_PASSPORT">International Passport</option>
          <option value="VOTERS_CARD">Voter's Card</option>
        </select>
      </div>

      <div className="kyc-form-group">
        <label>
          {identityInfo.idType === 'NATIONAL_ID' && 'National ID Number * (NIN)'}
          {identityInfo.idType === 'DRIVERS_LICENSE' && 'Driver\'s License Number *'}
          {identityInfo.idType === 'INTERNATIONAL_PASSPORT' && 'Passport Number *'}
          {identityInfo.idType === 'VOTERS_CARD' && 'Voter\'s Card Number *'}
        </label>
        <input
          type="text"
          value={identityInfo.idNumber}
          onChange={(e) => setIdentityInfo({ ...identityInfo, idNumber: e.target.value })}
          required
          placeholder={
            identityInfo.idType === 'NATIONAL_ID' ? 'Enter your 11-digit NIN' :
            identityInfo.idType === 'DRIVERS_LICENSE' ? 'Enter your Driver\'s License number' :
            identityInfo.idType === 'INTERNATIONAL_PASSPORT' ? 'Enter your Passport number' :
            'Enter your Voter\'s Card number'
          }
          maxLength={identityInfo.idType === 'NATIONAL_ID' ? '11' : undefined}
        />
      </div>

      <div className="kyc-divider">Upload Documents</div>

      <div className="kyc-form-group">
        <label>
          {identityInfo.idType === 'NATIONAL_ID' && 'National ID Card (Front) *'}
          {identityInfo.idType === 'DRIVERS_LICENSE' && 'Driver\'s License (Front) *'}
          {identityInfo.idType === 'INTERNATIONAL_PASSPORT' && 'Passport Photo Page *'}
          {identityInfo.idType === 'VOTERS_CARD' && 'Voter\'s Card (Front) *'}
        </label>
        <div className="kyc-file-upload">
          <input
            type="file"
            id="idDocument"
            accept="image/*,application/pdf"
            onChange={(e) => handleFileChange(e, 'idDocument')}
          />
          <label htmlFor="idDocument" className={`kyc-file-label${documents.idDocument ? ' has-file' : ''}`}>
            <Upload size={20} />
            {documents.idDocument ? documents.idDocument.name : 'Click to upload (required)'}
          </label>
        </div>
      </div>

      {identityInfo.idType !== 'INTERNATIONAL_PASSPORT' && (
        <div className="kyc-form-group">
          <label>
            {identityInfo.idType === 'NATIONAL_ID' && 'National ID Card (Back)'}
            {identityInfo.idType === 'DRIVERS_LICENSE' && 'Driver\'s License (Back)'}
            {identityInfo.idType === 'VOTERS_CARD' && 'Voter\'s Card (Back)'}
          </label>
          <div className="kyc-file-upload">
            <input
              type="file"
              id="idDocumentBack"
              accept="image/*,application/pdf"
              onChange={(e) => handleFileChange(e, 'idDocumentBack')}
            />
            <label htmlFor="idDocumentBack" className={`kyc-file-label${documents.idDocumentBack ? ' has-file' : ''}`}>
              <Upload size={20} />
              {documents.idDocumentBack ? documents.idDocumentBack.name : 'Click to upload (optional)'}
            </label>
          </div>
        </div>
      )}

      <div className="kyc-form-group">
        <label>Passport Photo *</label>
        <div className="kyc-file-upload">
          <input
            type="file"
            id="passportPhoto"
            accept="image/*"
            onChange={(e) => handleFileChange(e, 'passportPhoto')}
          />
          <label htmlFor="passportPhoto" className={`kyc-file-label${documents.passportPhoto ? ' has-file' : ''}`}>
            <Upload size={20} />
            {documents.passportPhoto ? documents.passportPhoto.name : 'Click to upload (required)'}
          </label>
        </div>
        <small className="kyc-hint">Clear photo of your face (not the same as ID photo)</small>
      </div>

      <div className="kyc-divider">Address Information</div>

      <div className="kyc-form-group">
        <label>Business Address *</label>
        <textarea
          value={identityInfo.businessAddress}
          onChange={(e) => setIdentityInfo({ ...identityInfo, businessAddress: e.target.value })}
          required
          placeholder="Enter your business address"
          rows="3"
        />
      </div>

      <div className="kyc-form-row">
        <div className="kyc-form-group">
          <label>City *</label>
          <input
            type="text"
            value={identityInfo.city}
            onChange={(e) => setIdentityInfo({ ...identityInfo, city: e.target.value })}
            required
          />
        </div>
        <div className="kyc-form-group">
          <label>State *</label>
          <input
            type="text"
            value={identityInfo.state}
            onChange={(e) => setIdentityInfo({ ...identityInfo, state: e.target.value })}
            required
          />
        </div>
      </div>

      {error && (
        <div className="kyc-alert error">
          <XCircle size={16} />
          {error}
        </div>
      )}

      {success && (
        <div className="kyc-alert success">
          <CheckCircle size={16} />
          {success}
        </div>
      )}

      <div className="kyc-form-actions">
        <button type="button" onClick={() => setCurrentStep(1)} className="kyc-btn secondary">
          <ArrowLeft size={20} />
          Back
        </button>
        <button type="submit" className="kyc-btn primary" disabled={loading}>
          {loading ? <Loader className="spin" size={20} /> : 'Submit for Review'}
          {!loading && <ArrowRight size={20} />}
        </button>
      </div>
    </form>
  );

  const renderStep3 = () => (
    <form onSubmit={submitBusinessInfo} className="kyc-form">
      <div className="kyc-form-header">
        <h2>Level 3: Full Business Verification</h2>
        <p>Get unlimited transaction limits</p>
      </div>

      <div className="kyc-form-group">
        <label>BVN (Bank Verification Number) *</label>
        <input
          type="text"
          value={businessInfo.bvn}
          onChange={(e) => setBusinessInfo({ ...businessInfo, bvn: e.target.value })}
          required
          maxLength="11"
          placeholder="11-digit BVN"
        />
        <small className="kyc-hint">Your BVN is safe and will be encrypted</small>
      </div>

      {basicInfo.isRegisteredBusiness && (
        <>
          <div className="kyc-form-group">
            <label>CAC Registration Number *</label>
            <input
              type="text"
              value={businessInfo.registrationNumber}
              onChange={(e) => setBusinessInfo({ ...businessInfo, registrationNumber: e.target.value })}
              required={basicInfo.isRegisteredBusiness}
              placeholder="RC Number"
            />
          </div>

          <div className="kyc-form-group">
            <label>CAC Certificate</label>
            <div className="kyc-file-upload">
              <input
                type="file"
                id="cacDocument"
                accept="image/*,application/pdf"
                onChange={(e) => handleFileChange(e, 'cacDocument')}
              />
              <label htmlFor="cacDocument" className={`kyc-file-label${documents.cacDocument ? ' has-file' : ''}`}>
                <Upload size={20} />
                {documents.cacDocument ? documents.cacDocument.name : 'Click to upload'}
              </label>
            </div>
          </div>
        </>
      )}

      <div className="kyc-form-group">
        <label>Tax Identification Number (TIN)</label>
        <input
          type="text"
          value={businessInfo.taxIdentificationNumber}
          onChange={(e) => setBusinessInfo({ ...businessInfo, taxIdentificationNumber: e.target.value })}
          placeholder="Optional"
        />
      </div>

      <div className="kyc-form-group">
        <label>Utility Bill / Proof of Address</label>
        <div className="kyc-file-upload">
          <input
            type="file"
            id="utilityBill"
            accept="image/*,application/pdf"
            onChange={(e) => handleFileChange(e, 'utilityBill')}
          />
          <label htmlFor="utilityBill" className={`kyc-file-label${documents.utilityBill ? ' has-file' : ''}`}>
            <Upload size={20} />
            {documents.utilityBill ? documents.utilityBill.name : 'Click to upload'}
          </label>
        </div>
        <small className="kyc-hint">Recent utility bill (not older than 3 months)</small>
      </div>

      {error && (
        <div className="kyc-alert error">
          <XCircle size={16} />
          {error}
        </div>
      )}

      {success && (
        <div className="kyc-alert success">
          <CheckCircle size={16} />
          {success}
        </div>
      )}

      <div className="kyc-form-actions">
        <button type="button" onClick={() => setCurrentStep(2)} className="kyc-btn secondary">
          <ArrowLeft size={20} />
          Back
        </button>
        <button type="submit" className="kyc-btn primary" disabled={loading}>
          {loading ? <Loader className="spin" size={20} /> : 'Submit Final KYC'}
          {!loading && <ArrowRight size={20} />}
        </button>
      </div>
    </form>
  );

  const renderStep4 = () => (
    <div className="kyc-complete">
      <div className="kyc-complete-icon">
        <CheckCircle size={64} />
      </div>
      <h2>KYC Verification Complete!</h2>
      <p>Your account has been fully verified. You can now enjoy:</p>
      <ul className="kyc-benefits">
        <li><CheckCircle size={16} /> Unlimited transaction limits</li>
        <li><CheckCircle size={16} /> Instant withdrawals</li>
        <li><CheckCircle size={16} /> Premium support</li>
        <li><CheckCircle size={16} /> Access to all features</li>
      </ul>
      {kycData?.status === 'PENDING_REVIEW' && (
        <div className="kyc-alert info">
          <AlertCircle size={16} />
          Your submission is pending admin review. You'll be notified once approved.
        </div>
      )}
    </div>
  );

  return (
    <div className="kyc-wizard-container">
      <div className="kyc-wizard">
        {renderStepIndicator()}

        {kycData?.progressPercentage !== undefined && (
          <div className="kyc-progress">
            <div className="kyc-progress-bar">
              <div
                className="kyc-progress-fill"
                style={{ width: `${kycData.progressPercentage}%` }}
              />
            </div>
            <span className="kyc-progress-text">{kycData.progressPercentage}% Complete</span>
          </div>
        )}

        <div className="kyc-content">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>
      </div>
    </div>
  );
};

export default KYCWizard;
